/**
 * "What People Say" — cross-site review aggregation shown on the PDP (SSD-5362).
 *
 * Domain entities only. HTTP wire types live in
 * `infrastructure/whatPeopleSay/utils/types.ts`.
 */

export interface WhatPeopleSayRating {
  /** Aggregate score across sources, e.g. 4.7 */
  score: number;
  /** Total review count across sources, e.g. 28000 */
  reviewCount: number;
  /** Share of reviewers recommending the product (0–100), e.g. 92 */
  recommendedPercent: number;
}

export interface WhatPeopleSaySummary {
  /** AI-generated summary text of aggregated reviews */
  text: string;
  /** Review sources the summary was built from, e.g. ['SkinSeoul', 'Hwahae'] */
  sources: string[];
}

export type WhatPeopleSayMentionSentiment = 'positive' | 'negative';

export interface WhatPeopleSayMention {
  /** Stable id from the wire (e.g. 'long-lasting-hydration') — used as the React key */
  id: string;
  /** Short mention label, e.g. 'Long-lasting hydration' */
  label: string;
  /** How many reviews mention it, e.g. 1240 */
  count: number;
  /**
   * Frequency wording the server picked for the requested locale, e.g. 'Often noted' or
   * '자주 언급됨'. Empty when the payload carries none — the meta line then drops the lead-in.
   *
   * The backend owns the tier rule (`ReviewInsightMentionFrequency`): it holds the share each
   * topic has of the product's reviews, and only it can apply the evidence-risk downgrade or
   * localize the copy. Never re-derive this from `count` on the client — the two rules would
   * drift and every non-English locale would fall back to English.
   */
  frequencyLabel: string;
  /** Representative review quotes for this mention (may be empty) */
  quotes: string[];
  sentiment: WhatPeopleSayMentionSentiment;
}

export interface WhatPeopleSayAttribute {
  /** Attribute name, e.g. 'Hydration' */
  name: string;
  /** Positive share for the attribute (0–100), e.g. 86 */
  percent: number;
}

export interface WhatPeopleSay {
  rating: WhatPeopleSayRating;
  summary: WhatPeopleSaySummary;
  /** Positive mentions ("What people loved") */
  loved: WhatPeopleSayMention[];
  /** Negative mentions ("Room to improve") */
  improvements: WhatPeopleSayMention[];
  /** Attribute breakdown ("Rated by attribute") */
  attributes: WhatPeopleSayAttribute[];
  /** One-sentence summary above the loved cards. Absent on backend v1 payloads. */
  lovedSummary?: string;
  /** One-sentence summary above the "Good to Know" cards. Absent on backend v1 payloads. */
  improvementsSummary?: string;
}

/**
 * Why the insight is (or is not) renderable. Mirrors the backend availability enum
 * so the PDP can distinguish "no section" from "still gathering reviews".
 */
export type WhatPeopleSayAvailability = 'ready' | 'not_mapped' | 'processing' | 'insufficient_evidence' | 'stale';

/**
 * Availability + payload. `data` is non-null only when the insight is fully renderable;
 * consumers branch on `availability` to choose between the populated card, the
 * "gathering reviews" card, and rendering nothing.
 */
export interface WhatPeopleSayInsight {
  availability: WhatPeopleSayAvailability;
  data: WhatPeopleSay | null;
}

/**
 * The language the insight copy is requested in — the user's own language, sent as-is.
 *
 * A bare `string`, not a union of the served languages: choosing the copy language for an
 * unserved language is the server's call, not ours. Narrowing here would mean re-encoding the
 * backend's list in two clients and rewriting a language the backend may already serve.
 */
export type WhatPeopleSayLocale = string;

/** Hook/domain query params (wire request mirrors this shape). */
export interface WhatPeopleSayQueryParams {
  /** Catalog product ID */
  productId: number;
  /** Display language; backend defaults to `en` */
  locale?: WhatPeopleSayLocale;
}
